import {
  ApiResponseWithPaging,
  Permission,
  Role,
  RoleOptions,
  RolesListFilter,
  RoleUpsertBody,
  UserOnRole,
} from '@dispatch/shared/types';
import { getPermission } from '../permissions/permissions.service';
import { dispatchClient } from '../clients';
import { mapResponseWithPaging } from '../helpers/AxiosMappers';

const AUTHORIZATION_ROLES_URL = `dispatch/auth/roles`;
const AUTHORIZATION_USER_ON_ROLES_URL = (roleId: string) =>
  `dispatch/auth/roles/${roleId}/users`;

export async function getRoles(
  filters?: RolesListFilter,
  continuationToken?: string
): Promise<ApiResponseWithPaging<Role[]>> {
  try {
    const response = await dispatchClient.get<Role[]>(
      `${AUTHORIZATION_ROLES_URL}`,
      {
        params: {
          ...filters,
        },
        headers: {
          'Continuation-Token': continuationToken || '',
        },
      }
    );

    return mapResponseWithPaging(response);
  } catch (e) {
    throw new Error('Errro fetching roles from API');
  }
}

export async function getRole(roleId: string) {
  return dispatchClient.get<Role>(`${AUTHORIZATION_ROLES_URL}/${roleId}`);
}

export async function upsertRole(postData: Partial<RoleUpsertBody>) {
  return dispatchClient.put<Role>(`${AUTHORIZATION_ROLES_URL}`, postData);
}

export async function getRolePermissions(
  permissionIds: string[]
): Promise<Permission[] | undefined> {
  try {
    return Promise.all(
      permissionIds?.flatMap(
        async (id: string): Promise<Permission | undefined> => {
          const { data } = await getPermission(id);
          return data;
        }
      )
    ).catch((errors) => {
      return errors;
    });
  } catch (errors: unknown) {
    /* eslint-disable no-console */
    console.error('error fetching roles from API', errors);
    return undefined;
  }
}

export async function getRoleUserCount(
  roleId: string
): Promise<number | undefined> {
  try {
    const response = await dispatchClient.get<UserOnRole[]>(
      `${AUTHORIZATION_USER_ON_ROLES_URL(roleId)}`
    );

    const roleUserCount: UserOnRole[] = response?.data;

    if (response.status === 200 && roleUserCount) {
      return roleUserCount?.length;
    }
  } catch (e) {
    /* eslint-disable no-console */
    console.error('error fetching roles user count from API', e);
  }
  return undefined;
}

export async function getRoleOptions(): Promise<RoleOptions[]> {
  try {
    const response = await dispatchClient.get<Role[]>(
      `${AUTHORIZATION_ROLES_URL}`
    );

    const roles: Role[] = response?.data;

    const roleOptions: Promise<RoleOptions>[] = roles.map(
      async (role: Role) => {
        return {
          title: role?.name || '',
          value: role.id,
          data: { ...role, permissions: role.permissions },
        };
      }
    );
    const data = await Promise.all(roleOptions);

    if (data) {
      return data;
    }
    return [];
  } catch (e) {
    throw new Error('Error fetching role options from API');
  }
}
